import * as React from "react";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { RobotIcon } from "@phosphor-icons/react/dist/ssr/Robot";
import { StorefrontIcon } from "@phosphor-icons/react/dist/ssr/Storefront";

import { useTemplateAgents, type PlatformSetting } from "@/hooks/api/use-platform-settings";

import { AGENT_TEMPLATE_PREFIX, BUSINESS_TYPES } from "./lib";

export interface AgentTemplatesSectionProps {
	items: PlatformSetting[];
	drafts: Record<string, string>;
	disabled: boolean;
	onChange: (key: string, value: string) => void;
}

// Card per business type mapping it to an agent that lives in the AX Platform
// workspace tenant. Provisioning clones the mapped agent into new tenants.
export function AgentTemplatesSection({ items, drafts, disabled, onChange }: AgentTemplatesSectionProps): React.JSX.Element {
	const templateAgentsQuery = useTemplateAgents();
	const templateAgents = templateAgentsQuery.data ?? [];

	const templateItems = items.filter((item) => item.key.startsWith(AGENT_TEMPLATE_PREFIX));

	return (
		<Stack spacing={2}>
			<Alert severity="info">
				Templates live in the AX Platform workspace — switch account to edit them; map them here.
			</Alert>

			{templateAgentsQuery.isPending ? (
				<Stack spacing={1.5}>
					{[0, 1, 2].map((index) => (
						<Skeleton height={72} key={index} variant="rounded" />
					))}
				</Stack>
			) : null}

			{!templateAgentsQuery.isPending && templateAgentsQuery.data === null ? (
				<Typography color="text.secondary" variant="body2">
					The template agents API is not available yet.
				</Typography>
			) : null}

			{!templateAgentsQuery.isPending && templateAgentsQuery.data !== null && templateAgents.length === 0 ? (
				<Card sx={{ p: 3, textAlign: "center" }} variant="outlined">
					<Stack spacing={1} sx={{ alignItems: "center" }}>
						<Avatar sx={{ bgcolor: "var(--mui-palette-background-level2)", color: "var(--mui-palette-text-secondary)" }}>
							<RobotIcon fontSize="var(--Icon-fontSize)" />
						</Avatar>
						<Typography variant="subtitle1">No workspace agents yet</Typography>
						<Typography color="text.secondary" variant="body2" component="div" sx={{ maxWidth: 440 }}>
							<Box component="ol" sx={{ m: 0, pl: 3, textAlign: "left" }}>
								<li>Open the account switcher and select the AX Platform workspace.</li>
								<li>Create an agent there and set up its prompt and services.</li>
								<li>Come back here and map it to a business type.</li>
							</Box>
						</Typography>
					</Stack>
				</Card>
			) : null}

			{templateItems.map((item) => {
				const vertical = item.key.slice(AGENT_TEMPLATE_PREFIX.length);
				const meta = BUSINESS_TYPES[vertical];
				const TypeIcon = meta?.icon ?? StorefrontIcon;
				const selectedId = drafts[item.key] ?? item.value ?? "";
				// Keep the current mapping selectable (and MUI's Select value in
				// range) even when the agent list is still loading or the mapped
				// agent no longer exists in the platform workspace.
				const isUnknownAgent = selectedId.length > 0 && !templateAgents.some((agent) => agent.id === selectedId);

				return (
					<Card key={item.key} variant="outlined">
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={2}
							sx={{ alignItems: { sm: "center" }, p: 2 }}
						>
							<Avatar
								sx={{
									bgcolor: "var(--mui-palette-primary-selected)",
									color: "var(--mui-palette-primary-main)",
									display: { xs: "none", sm: "flex" },
								}}
								variant="rounded"
							>
								<TypeIcon fontSize="var(--Icon-fontSize)" />
							</Avatar>
							<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
								<Typography variant="subtitle2">{meta?.title ?? item.label}</Typography>
								<Typography color="text.secondary" variant="caption">
									{meta?.description ?? "New signups of this type start from this agent."}
								</Typography>
							</Box>
							<FormControl size="small" sx={{ flexShrink: 0, width: { xs: "100%", sm: 260 } }}>
								<Select
									disabled={disabled}
									displayEmpty
									onChange={(event) => onChange(item.key, event.target.value)}
									value={selectedId}
								>
									<MenuItem value="">
										<em>Not set</em>
									</MenuItem>
									{isUnknownAgent ? (
										<MenuItem value={selectedId}>
											{templateAgentsQuery.isPending ? selectedId : `Unknown agent (${selectedId})`}
										</MenuItem>
									) : null}
									{templateAgents.map((agent) => (
										<MenuItem key={agent.id} value={agent.id}>
											{agent.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>
					</Card>
				);
			})}
		</Stack>
	);
}
